<template>
    <v-col cols="12" sm="6" md="6" lg="3" class="mx-auto mt-5">
        <v-card>
            <v-toolbar color="primary" dark flat>
                <v-toolbar-title>Register</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
                <v-alert
                        dense
                        outlined
                        type="error" v-show="errors.length">
                    <ul>
                        <li v-for="error in errors" :key="error">{{ error }}</li>
                    </ul>
                </v-alert>
                <v-form>
                    <v-text-field
                        tabindex="1"
                        v-model="firstName"
                        type="text"
                        label="First name"
                        :error-messages="firstNameErrors"
                        @input="$v.firstName.$touch()"
                        @blur="$v.firstName.$touch()"
                        prepend-icon="mdi-account">
                    </v-text-field>
                    <v-text-field
                        tabindex="2"
                        v-model="lastName"
                        type="text"
                        label="Last name"
                        :error-messages="lastNameErrors"
                        @input="$v.lastName.$touch()"
                        @blur="$v.lastName.$touch()"
                        prepend-icon="mdi-account">
                    </v-text-field>
                    <v-text-field
                        tabindex="3"
                        v-model="email"
                        type="email"
                        label="Email"
                        :error-messages="emailErrors"
                        @input="$v.email.$touch()"
                        @blur="$v.email.$touch()"
                        prepend-icon="mdi-at">
                    </v-text-field>
                    <v-text-field
                            tabindex="4"
                            v-model="password"
                            :type="showPassword ? 'text' : 'password'"
                            label="Password"
                            prepend-icon="mdi-lock"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            :error-messages="passwordErrors"
                            @input="$v.password.$touch()"
                            @blur="$v.password.$touch()">
                    </v-text-field>
                    <p class="caption mb-0">Password rules:</p>
                    <ul class="mb-5">
                        <li class="caption" v-for="rule in passwordRules" :key="rule">{{ rule }}</li>
                    </ul>
                    <v-text-field
                        tabindex="5"
                        v-model="passwordConfirm"
                        :type="showRepeatPassword ? 'text' : 'password'"
                        label="Repeat password"
                        prepend-icon="mdi-lock"
                        :append-icon="showRepeatPassword ? 'mdi-eye' : 'mdi-eye-off'"
                        @click:append="showRepeatPassword = !showRepeatPassword"
                        :error-messages="confirmPasswordErrors"
                        @input="$v.passwordConfirm.$touch()"
                        @blur="$v.passwordConfirm.$touch()">
                    </v-text-field>
                </v-form>
                <div>
                  This site is protected by reCAPTCHA and the Google
                  <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                  <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-btn color="info" :to="{ name: 'login'}">Login</v-btn>
                <v-spacer></v-spacer>
                <v-btn color="success" @click="submit">Register</v-btn>
            </v-card-actions>
        </v-card>
    </v-col>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators'

export default {
    mixins: [validationMixin],
    name: 'LoginPage',
    data() {
        return {
            showPassword: false,
            showRepeatPassword: false,
            email: '',
            password: '',
            passwordConfirm: '',
            firstName: '',
            lastName: '',
            passwordRules: [
                'one letter',
                'one number',
                'mixture of both uppercase and lowercase letters',
                'at least 6 characters',
            ],
            errors: []
        }
    },
    computed: {
        emailErrors() {
            const errors = [];
            if (!this.$v.email.$dirty) return errors;
            !this.$v.email.email && errors.push('Must be valid e-mail');
            !this.$v.email.required && errors.push('E-mail is required');
            return errors;
        },
        passwordErrors() {
            const errors = [];
            if (!this.$v.password.$dirty) return errors;
            !this.$v.password.required && errors.push('Password is required');
            return errors;
        },
        confirmPasswordErrors() {
            const errors = [];
            if (!this.$v.passwordConfirm.$dirty) return errors;
            !this.$v.passwordConfirm.required && errors.push('Password confirm is required');
            return errors;
        },
        firstNameErrors() {
            const errors = [];
            if (!this.$v.firstName.$dirty) return errors;
            !this.$v.firstName.minLength && errors.push('First name must be at least 2 characters long.');
            !this.$v.firstName.maxLength && errors.push('First name must be less than 150 characters.');
            !this.$v.firstName.required && errors.push('First name is required');
            return errors;
        },
        lastNameErrors() {
            const errors = [];
            if (!this.$v.lastName.$dirty) return errors;
            !this.$v.lastName.minLength && errors.push('First name must be at least 2 characters long.');
            !this.$v.lastName.maxLength && errors.push('First name must be less than 150 characters.');
            !this.$v.lastName.required && errors.push('Last name is required');
            return errors;
        }
    },
    validations: {
        email: { required, email },
        password: { required },
        passwordConfirm: { required },
        firstName: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(150)
        },
        lastName: {
            required,
            minLength: minLength(2),
            maxLength: maxLength(150)
        }
    },
    methods: {
        async submit() {
            this.$v.$touch();

            if (!this.$v.$error) {
                await this.$recaptchaLoaded();

                // Execute reCAPTCHA with action "register".
                const token = await this.$recaptcha('register');
                const loginToken = await this.$recaptcha('login');

                this.$store.dispatch('register', {
                    first_name: this.firstName,
                    last_name: this.lastName,
                    email: this.email,
                    password: this.password,
                    password_confirmation: this.passwordConfirm,
                    '_recaptcha': token,
                    login_token: loginToken
                }).catch(result => {
                    if (result.response.status === 401) {
                        this.errors = [result.response.data.message];
                    } else {
                        var errors = result.response.data.errors;
                        let keys = Object.keys(errors);
                        this.errors = [];

                        keys.forEach(key => {
                            let item = errors[key];
                            item.forEach(error => {
                              this.errors.push(error);
                            })
                        })
                    }

                });
            }
        },
    },
}
</script>

<style>

</style>